import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import styled from '@emotion/styled';
import SphereMenu, { MenuItem as SphereMenuItem } from '../SphereMenu'; // Adjusted path
import ContentArea from '../ContentArea'; // Adjusted path
import CircuitBackground from '../CircuitBackground'; // Adjusted path


// フローティングボタンのスタイル
const FloatingButton = styled(motion.a)`
  position: fixed;
  right: 40px;
  bottom: 40px;
  background-color: rgba(0, 255, 255, 0.7);
  color: #fff;
  padding: 15px 30px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: bold;
  box-shadow: 0 5px 15px rgba(0, 255, 255, 0.4);
  transition: all 0.3s ease;
  z-index: 1000; // 他の要素より手前に表示

  &:hover {
    background-color: rgba(0, 255, 255, 1);
    box-shadow: 0 8px 25px rgba(0, 255, 255, 0.6);
    transform: translateY(-5px);
  }
`;

const SpherePage: React.FC = () => {
    const [activeSphereItemPath, setActiveSphereItemPath] = useState<string | null>(null);
    const [selectedSphereItemData, setSelectedSphereItemData] = useState<SphereMenuItem | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const ref = useRef(null); // For initial message animation
    const isInView = useInView(ref, { once: true }); // For initial message animation

    const initialMessages = [
        'ここはLudyWorksのホームページ。',
        'デジタル空間でのインタラクティブな体験を提供します。',
        'Sphereをクリックして、コンテンツを表示してください。',
    ];
    const InitialMessageStyle = styled.div`
    position: absolute;
    width: 40vw;
    top: 50vh;
    left: 40vw;
    color: #fff;
    font-size: 24px;
    text-align: center;
    z-index: 10;
    pointer-events: none;
    @media screen and (max-width: 1124px) and (min-width: 769px) {
      font-size: 20px;
      width: 80vw;
      top: 50vh;
      left: 10vw;
    }
    @media screen and (max-width: 768px) {
      font-size: 12px;
      width: 90vw;
      top: 50vh;
      left: 5vw;
    }
  `;

    // Define menu items here or import them if they are shared with SphereMenu itself
    // This is crucial for the useEffect hook to find the matching item on direct navigation
    const sphereMenuItems: SphereMenuItem[] = [
        { id: 'home', name: 'HOME', position: [0, 2, 0], path: '/home' },
        { id: 'philosophy', name: 'PHILOSOPHY', position: [2, 0, 0], path: '/philosophy' },
        { id: 'works', name: 'WORKS', position: [-2, 0, 0], path: '/works' },
        { id: 'profile', name: 'PROFILE', position: [0, -2, 0], path: '/profile' },
        { id: 'recruitment', name: 'RECRUITMENT', position: [0, 0, -2], path: '/recruitment' },
        { id: 'contact', name: 'CONTACT', position: [0, 0, 2], path: '/contact' },
        { id: 'blog', name: 'BLOG', position: [1.5, 1.5, 0], path: '/blog' },
        // Add other items as defined in your SphereMenu component
    ];

    const handleSphereItemSelected = (item: SphereMenuItem) => {
        console.log('SpherePage: Item selected:', item.name);
        setSelectedSphereItemData(item);
        setActiveSphereItemPath(item.path);
        if (location.pathname !== item.path) {
            navigate(item.path); // Update URL to match selected item
        }
    };

    const handleCloseContentArea = () => {
        console.log('SpherePage: Closing content area');
        setSelectedSphereItemData(null);
        setActiveSphereItemPath(null);
        navigate('/'); // Navigate to the default sphere view (e.g., home)
        return
    };

    useEffect(() => {
        // location.pathnameに基づいてselectedSphereItemDataを更新
        const matchingItem = sphereMenuItems.find(item => item.path === location.pathname);
        if (matchingItem) {
            setSelectedSphereItemData(matchingItem);
            setActiveSphereItemPath(matchingItem.path);
        } else {
            setSelectedSphereItemData(null);
            setActiveSphereItemPath(null);
        }
    }, [location.pathname]);

    const getPageTitle = (path: string) => {
        const item = sphereMenuItems.find(item => item.path === path);
        return item ? `${item.name} | LudyWorks` : 'LudyWorks | Web制作・システム開発';
    };

    const getPageDescription = (path: string) => {
        switch (path) {
            case '/home':
                return 'LudyWorksの公式サイト。インタラクティブなWeb体験とシステム開発を提供します。';
            case '/philosophy':
                return '私たちの理念とビジョンについて。テクノロジーとデザインで未来を創造します。';
            case '/works':
                return '制作実績一覧。Webサイト、アプリケーション開発など、様々なプロジェクトをご覧ください。';
            case '/profile':
                return 'LudyWorksの会社概要。所在地、設立日、事業内容など。';
            case '/recruitment':
                return '採用情報。私たちと一緒に新しい価値を創造しませんか。';
            case '/contact':
                return 'お問い合わせフォーム。ご相談、お見積もりなどお気軽にご連絡ください。';
            default:
                return 'Web集客、システム開発でお客様の事業をサポートします。';
        }
    };

    return (
        <div className="SpherePageApp" style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
            <Helmet>
                <title>{getPageTitle(location.pathname)}</title>
                <meta name="description" content={getPageDescription(location.pathname)} />
                <meta property="og:title" content={getPageTitle(location.pathname)} />
                <meta property="og:description" content={getPageDescription(location.pathname)} />
                <meta property="og:url" content={`https://ludyworks.com${location.pathname}`} />
            </Helmet>
            <CircuitBackground />
            <SphereMenu
                onItemSelected={handleSphereItemSelected}
                activeItemPath={activeSphereItemPath}
            // SphereMenuが必要とする場合、menuItemsを外部から渡す
            />
            <AnimatePresence>
                {selectedSphereItemData && selectedSphereItemData.path !== '/' && selectedSphereItemData.path !== '/blog' && (
                    <motion.div
                        key={selectedSphereItemData.path} // Use path for key
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.3 }}
                        // SpherePageApp内でContentAreaの位置をラップするmotion.div
                        style={{
                            position: 'absolute', // Ensure it's positioned correctly within SpherePageApp
                            top: 0, left: 0, right: 0, bottom: 0, // Cover the area
                            display: 'flex', alignItems: 'center', justifyContent: 'center', // Center ContentArea
                            zIndex: 1,
                        }}
                    >
                        <ContentArea
                            itemData={selectedSphereItemData}
                            onClose={handleCloseContentArea}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
            {!selectedSphereItemData && location.pathname === '/' && ( // 初期メッセージは何も選択されていないときにルートでのみ表示
                <InitialMessageStyle>
                    {initialMessages.map((message, i) => (
                        <div
                            key={i}
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-start', // Or 'center' depending on desired alignment
                                alignItems: 'center',
                                marginBottom: '10px', // 各行の間にスペースを追加
                            }}
                        >
                            {/* 各文字のテキストアニメーション */}
                            {message.split('').map((char, j) => (
                                <motion.p
                                    ref={ref} // This ref might cause issues if used for multiple elements; consider using variants
                                    key={`${i}-${j}`}
                                    initial={{ opacity: 0, x: -18 }}
                                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                                    transition={{ duration: 0.5, delay: (i * message.length + j) * 0.05 }} // Adjusted delay
                                    style={{
                                        display: 'inline-block',
                                        textShadow: '0 0 5px rgba(0, 255, 255, 0.8)',
                                        color: '#fff',
                                        margin: 0,
                                    }}
                                >
                                    {char === ' ' ? <span>&nbsp;</span> : char}
                                </motion.p>
                            ))}
                        </div>
                    ))}
                </InitialMessageStyle>
            )}
            {/* サービスのランディングページへのフローティングボタン */}
            <FloatingButton
                href="/nexti_landingpage" // サービスのランディングページのパスを指定
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 1 }} // 少し遅延させて表示
            >
                インフォメーションハブについて
            </FloatingButton>
        </div>
    );
};

export default SpherePage;
